import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { describe } from '@/lib/errors';
import { useAuth } from '@/stores/auth';

export function LoginPage() {
  const login = useAuth((s) => s.login);
  const completeMfa = useAuth((s) => s.completeMfa);
  const mfaToken = useAuth((s) => s.mfaToken);
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const destination = (location.state as { from?: string } | null)?.from ?? '/';

  async function submitPassword(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await login(email, password);
      if (result === 'ok') navigate(destination, { replace: true });
    } catch (err) {
      setError(describe(err));
    } finally {
      setBusy(false);
      setPassword('');
    }
  }

  async function submitCode(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await completeMfa(code);
      navigate(destination, { replace: true });
    } catch (err) {
      setError(describe(err));
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-brand">
          <h1>Hartos</h1>
          <small>Spot trading console</small>
        </div>

        <div className="panel">
          <div className="panel-head">
            <h2>{mfaToken ? 'Enter your code' : 'Sign in'}</h2>
          </div>
          <div className="panel-body">
            {error && (
              <div className="notice" data-tone="error" role="alert">
                {error}
              </div>
            )}

            {mfaToken ? (
              <form onSubmit={submitCode}>
                <label htmlFor="code">Authentication code</label>
                <input
                  id="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                />
                <p className="hint" style={{ marginTop: 'var(--space-2)' }}>
                  Six digits from your authenticator, or one of your recovery codes.
                </p>
                <button
                  type="submit"
                  className="primary"
                  disabled={busy || code.length < 6}
                  style={{ width: '100%', marginTop: 'var(--space-4)' }}
                >
                  {busy ? 'Checking…' : 'Continue'}
                </button>
              </form>
            ) : (
              <form onSubmit={submitPassword}>
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  autoComplete="username"
                  autoFocus
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
                <label htmlFor="password" style={{ marginTop: 'var(--space-4)' }}>
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
                <button
                  type="submit"
                  className="primary"
                  disabled={busy || !email || !password}
                  style={{ width: '100%', marginTop: 'var(--space-5)' }}
                >
                  {busy ? 'Signing in…' : 'Sign in'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
